import { useEffect, useId, useState } from "react";
import { COLOR, FONT } from "../lib/tokens";
import { pickCookColor } from "../lib/cookColors";
import { paletteEntry } from "../lib/participantPalette";
import { absoluteAvatarURL } from "../lib/absoluteAvatarURL";
import { supabase } from "../lib/supabase";
import { BoodaTimer } from "../lib/boodaTimer";

// CookActivityCard — the in-app, JSX mirror of the iOS Lock-Screen
// Live Activity card from `BoodaTimerLiveActivity.swift`.
//
// One per active cook. Vertical-list-friendly. Renders:
//   • Status dot in the cook's color + recipe title (uppercase, mono)
//   • Headline row: current step name + active timer countdown
//   • Sub-line: total recipe-time-remaining
//   • Proportional time-weighted progress bar:
//       - Past step segments fill solid in the cook's color.
//       - Active segment: faint background + ticking fill from left
//         (driven by setInterval against the timer's endsAt).
//       - Future segments: faint owner-tinted track.
//       - Boundary dots (small filled circles in the cook's color)
//         between segments.
//   • Tap → openCookMode(recipe, { joinSessionId }).
//
// NO emojis. NO claim button. Mirrors what the user already sees on
// their lock screen for each parallel cook.
//
// Props:
//   cook            — { session, recipe, activeStep, timerEndsAt }
//                     (entry shape from useActiveCookSessions)
//   onTap           — fn(recipe, opts) → opens that cook's CookMode
//   ownerName       — optional first name to show under the title
//   ownerAvatarUrl  — optional profile-pic URL; renders inside the
//                     ticker avatar on the progress bar. Falls back
//                     to the owner-name initial when missing.

function fmtMmSs(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—:—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtHmS(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Sum of step.timer (or s.timers stack) per step. Returns numeric
// seconds per step in an array; missing timer → 0.
function stepSeconds(step) {
  if (Array.isArray(step?.timers) && step.timers.length > 0) {
    let total = 0;
    for (const t of step.timers) total += Math.max(0, Number(t?.seconds) || 0);
    return total;
  }
  return Math.max(0, Number(step?.timer) || 0);
}

export default function CookActivityCard({ cook, onTap, ownerName, ownerAvatarUrl }) {
  const session = cook?.session;
  const recipe = cook?.recipe;
  const activeStep = cook?.activeStep;
  const timerEndsAt = cook?.timerEndsAt;

  // 1Hz tick — always running so the avatar+countdown advance even
  // when there's no `cook_step_notifications` row to anchor against
  // (we fall back to `cook_session_steps.started_at` + the recipe
  // step's defined timer below). Without the always-on interval the
  // ticker freezes on any cook whose current step's timer hasn't
  // been started, even though the step is "in progress."
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Pull every open step row that has a future endsAt — the card's
  // primary countdown is the active step's timer, and any OTHER
  // running timers (parallel work across steps) get listed inline
  // next to the Total line. Without this the card would only show
  // ONE timer at a time even when the cook has multiple steps
  // ticking in parallel.
  // Channel name MUST be unique per mounted card. Two CookActivityCard
  // instances can render at once (Home top + Cooks sub-tab), and a
  // shared `cookcard:${sessionId}` name makes Supabase reject the
  // second .on() after the first .subscribe() with "cannot add
  // postgres_changes callbacks ... after subscribe()". useId gives a
  // stable per-instance id; combined with the session id we get a
  // channel name that's unique per (instance × session).
  const instanceId = useId();
  const [parallelTimers, setParallelTimers] = useState([]);
  // Multi-participant pin support. Each entry = one cook on this
  // session: their open step row, color_tag-derived palette, and
  // avatar. Replaces the previous single-owner model where the bar
  // only ever showed ONE avatar in the session's session-id-hashed
  // color. Mirrors the iOS Live Activity's helpers[] projection so
  // the in-app card matches what's on the lock screen.
  const [participantPins, setParticipantPins] = useState([]);
  useEffect(() => {
    if (!session?.id) return undefined;
    let alive = true;
    const fetchAll = async () => {
      const [stepsRes, partsRes, assignRes] = await Promise.all([
        supabase
          .from("cook_session_steps")
          .select("step_id, user_id, started_at, finished_at, nominal_seconds, timer_state")
          .eq("cook_session_id", session.id),
        supabase
          .from("cook_session_participants")
          .select("user_id, color_tag")
          .eq("cook_session_id", session.id)
          .is("left_at", null),
        // Assignments override the cook_session_steps.user_id —
        // if Doug was assigned step 1, the bar should show Doug's
        // avatar there even if Trevor (lead) navigated into the
        // step and created the row.
        supabase
          .from("cook_step_assignments")
          .select("step_id, assigned_to, status")
          .eq("cook_session_id", session.id)
          .in("status", ["pending", "accepted", "in_progress"]),
      ]);
      if (!alive) return;
      const allSteps = stepsRes.data || [];
      const parts = partsRes.data || [];
      const assignments = assignRes.data || [];

      // Build step_id → assignee_id map for quick lookup. Strip the
      // TimerStack stage suffix so an assignment on "step2" applies
      // to all stages "step2-s0", "step2-s1", etc.
      const assigneeByStep = new Map();
      for (const a of assignments) {
        if (!a.assigned_to || !a.step_id) continue;
        const baseId = String(a.step_id).replace(/-s\d+$/, "");
        assigneeByStep.set(baseId, a.assigned_to);
      }

      // parallelTimers — keep existing semantics: open rows with
      // future endsAt (used for the "(N) m:ss" parallel-timer
      // strip below the headline).
      const nowMs = Date.now();
      const active = allSteps.filter((r) => {
        if (r.finished_at) return false;
        const ends = r?.timer_state?.endsAt;
        if (!ends || r.timer_state?.isPaused) return false;
        const endsMs = new Date(ends).getTime();
        return Number.isFinite(endsMs) && endsMs > nowMs;
      });
      setParallelTimers(active);

      // Fetch profiles for all participants (lead + assignees).
      const userIds = new Set();
      for (const p of parts) if (p.user_id) userIds.add(p.user_id);
      for (const a of assignments) if (a.assigned_to) userIds.add(a.assigned_to);
      if (session.user_id) userIds.add(session.user_id);
      let profilesById = new Map();
      if (userIds.size > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, name, avatar_url")
          .in("id", Array.from(userIds));
        for (const p of (profs || [])) profilesById.set(p.id, p);
      }
      const colorByUser = new Map();
      for (const p of parts) colorByUser.set(p.user_id, p.color_tag);
      // Lead falls back to session.id-hashed color if not in
      // participants (older sessions). Newer sessions always have
      // the lead in participants with their own color_tag.
      const leadColorTag = colorByUser.get(session.user_id);

      // Pin-resolution priority:
      //   1. ASSIGNMENTS first — if a step has an assignee, that
      //      person OWNS the pin at that step regardless of who
      //      created the cook_session_steps row. Doug-assigned →
      //      Doug's avatar/color, even if Trevor (lead) navigated
      //      into the step and his user_id sits on the row.
      //   2. Fall through to per-user step rows for any user who
      //      has open rows but isn't covered by an assignment.
      //   3. Unstarted participants (in the session, no rows, no
      //      assignment) get parked at the bar's start.
      const buildRowForUser = (userId) => {
        const prof = profilesById.get(userId);
        const tag = colorByUser.get(userId) || (userId === session.user_id ? leadColorTag : null);
        const palette = paletteEntry(tag);
        return {
          userId,
          name: (prof?.name || "").split(/\s+/)[0] || "Cook",
          color: palette.ring,
          tint: palette.bg,
          avatarUrl: absoluteAvatarURL(prof?.avatar_url),
          stepId: null,
          startedAt: null,
          timerEndsAt: null,
          nominalSec: 0,
          finished: false,
        };
      };

      const pins = [];
      const claimedUsers = new Set();

      // Pass 1: assignment-driven pins. Each assignment becomes a pin
      // for the ASSIGNEE at the assigned step. The step row (if any)
      // contributes timer state but the assignee's identity wins.
      for (const a of assignments) {
        if (!a.assigned_to || !a.step_id) continue;
        const baseId = String(a.step_id).replace(/-s\d+$/, "");
        // Find a matching cook_session_steps row for timer state
        // (most recent for this step, prefer open).
        const matchingRows = allSteps.filter(
          (r) => String(r.step_id || "").replace(/-s\d+$/, "") === baseId,
        );
        const open = matchingRows.filter((r) => !r.finished_at);
        const sortedDesc = (open.length > 0 ? open : matchingRows)
          .slice()
          .sort((a, b) => {
            const aMs = a.started_at ? new Date(a.started_at).getTime() : 0;
            const bMs = b.started_at ? new Date(b.started_at).getTime() : 0;
            return bMs - aMs;
          });
        const row = sortedDesc[0] || null;
        const base = buildRowForUser(a.assigned_to);
        pins.push({
          ...base,
          stepId: a.step_id,
          startedAt: row?.started_at || null,
          timerEndsAt: row?.timer_state?.endsAt || null,
          nominalSec: Number(row?.nominal_seconds) || 0,
          finished: !!row?.finished_at,
        });
        claimedUsers.add(a.assigned_to);
      }

      // Pass 2: per-user step rows for users NOT covered by an
      // assignment. Keeps the multi-cook case working when nobody
      // assigned the steps (lead just navigates through them).
      const rowsByUser = new Map();
      for (const r of allSteps) {
        if (!r.user_id) continue;
        const baseId = String(r.step_id || "").replace(/-s\d+$/, "");
        // Skip rows for steps that an assignment already covers —
        // the assignee's pin handles that segment.
        if (assigneeByStep.has(baseId)) continue;
        const list = rowsByUser.get(r.user_id) || [];
        list.push(r);
        rowsByUser.set(r.user_id, list);
      }
      for (const userId of Array.from(userIds)) {
        if (claimedUsers.has(userId)) continue;
        const rows = rowsByUser.get(userId) || [];
        if (rows.length === 0) {
          pins.push(buildRowForUser(userId));
          continue;
        }
        const open = rows.filter((r) => !r.finished_at);
        const sortedDesc = (open.length > 0 ? open : rows)
          .slice()
          .sort((a, b) => {
            const aMs = a.started_at ? new Date(a.started_at).getTime() : 0;
            const bMs = b.started_at ? new Date(b.started_at).getTime() : 0;
            return bMs - aMs;
          });
        const pick = sortedDesc[0];
        const base = buildRowForUser(userId);
        pins.push({
          ...base,
          stepId: pick.step_id || null,
          startedAt: pick.started_at || null,
          timerEndsAt: pick.timer_state?.endsAt || null,
          nominalSec: Number(pick.nominal_seconds) || 0,
          finished: !!pick.finished_at,
        });
      }
      setParticipantPins(pins);
    };
    fetchAll();
    const ch = supabase
      .channel(`cookcard:${session.id}:${instanceId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps", filter: `cook_session_id=eq.${session.id}` },
        () => fetchAll(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_participants", filter: `cook_session_id=eq.${session.id}` },
        () => fetchAll(),
      )
      .subscribe();
    return () => { alive = false; try { supabase.removeChannel(ch); } catch { /* ignore */ } };
  }, [session?.id, session?.user_id, instanceId]);

  if (!session) return null;
  const palette = pickCookColor(session.id);
  const recipeTitle = (recipe?.title || session?.recipe_title || "Cooking").toUpperCase();

  // Steps + per-step seconds for the proportional bar. Source order:
  //   1. recipe.steps — full recipe shape (preferred when resolved)
  //   2. session.step_summary — inline snapshot written at cook-start
  //      (migration 0235). Lets the card render total time + step
  //      labels even when the recipe never resolves (user_recipes
  //      cache lag, slug collision, recipe deletion). Matches what
  //      the iOS Live Activity already gets inline.
  //   3. [] — pre-0235 sessions where neither resolves; card renders
  //      "Ready to cook" + collapsed bar (today's behavior).
  const recipeSteps = recipe?.steps;
  const snapshotSteps = Array.isArray(session?.step_summary) ? session.step_summary : null;
  const steps = Array.isArray(recipeSteps) && recipeSteps.length > 0
    ? recipeSteps
    : (snapshotSteps && snapshotSteps.length > 0 ? snapshotSteps : []);
  const perStepSec = steps.map(stepSeconds);
  const totalSec = perStepSec.reduce((a, b) => a + b, 0) || 1;

  // Locate the active step (and its stage) by step_id. Telemetry
  // step_id may carry a `-s${stageIdx}` suffix on TimerStack stages;
  // strip it before matching against the recipe's step ids.
  const baseStepId = String(activeStep?.step_id || "").replace(/-s\d+$/, "");
  const activeStepIdx = activeStep && steps.length > 0
    ? steps.findIndex((s) => String(s.id) === baseStepId)
    : -1;

  // Active-step timing — three-tier resolver so the ticker keeps
  // moving across all the in-the-wild cases:
  //
  //   1. `timerEndsAt` (cook_step_notifications) — the most precise
  //      anchor: a server-side row with the exact deadline. Used
  //      whenever a timer is actively counting down on this step.
  //   2. `activeStep.started_at` + the recipe step's own timer
  //      seconds — fallback when the user hit "next" on a step but
  //      hasn't started a discrete timer yet (or the step is mid-
  //      stage on a TimerStack and the notification was already
  //      drained). Lets the avatar still advance based on wall-
  //      time elapsed since the step opened.
  //   3. Static at the start of the active segment — last resort
  //      when neither anchor exists.
  //
  // remainingSec is null when we can't compute one — the headline
  // mm:ss falls back to "—:—" in that case but the avatar still
  // moves because activeStepPct is derived from elapsedSec.
  const stepNominalFromRecipe = activeStepIdx >= 0
    ? perStepSec[activeStepIdx]
    : 0;
  const nominalSec = Number(activeStep?.nominal_seconds)
    || stepNominalFromRecipe
    || 0;
  // Headline timer: ONLY count down once a real timer is ticking
  // (timer_state.endsAt is set). The previous tier-2 fallback used
  // activeStep.started_at (step ENTRY time) to derive remaining,
  // which made the bar auto-tick the moment someone navigated into
  // a step they hadn't started — wrong when the step is assigned
  // to a different cook who hasn't tapped Start yet.
  let elapsedSec = 0;
  let remainingSec = null;
  if (timerEndsAt && nominalSec > 0) {
    const endsMs = new Date(timerEndsAt).getTime();
    remainingSec = Math.max(0, Math.ceil((endsMs - now) / 1000));
    elapsedSec = Math.max(0, Math.min(nominalSec, nominalSec - remainingSec));
  }
  const activeStepPct = nominalSec > 0 && timerEndsAt
    ? Math.max(0, Math.min(1, elapsedSec / nominalSec))
    : 0;

  // Total recipe time remaining = active-step remaining + sum of
  // upcoming steps' nominal seconds.
  const upcomingSec = activeStepIdx >= 0
    ? perStepSec.slice(activeStepIdx + 1).reduce((a, b) => a + b, 0)
    : perStepSec.reduce((a, b) => a + b, 0);
  const totalRemaining = (remainingSec || 0) + upcomingSec;

  // Headline text
  const stepLabel = activeStepIdx >= 0
    ? `Step ${activeStepIdx + 1} · ${steps[activeStepIdx]?.title || ""}`
    : "Ready to cook";

  return (
    <button
      type="button"
      onClick={() => {
        // Bump THIS cook's Live Activity priority so iOS swaps it into
        // the Dynamic Island compact slot — the user's attention has
        // shifted here, the island should follow. Fire-and-forget;
        // failure is non-fatal (e.g. running on a non-Dynamic-Island
        // device, or activity already prioritized).
        BoodaTimer.focusActivity({ clientId: `cook-${session.id}` })
          .catch((e) => console.warn("[cookCard] focusActivity failed:", e?.message || e));
        if (recipe && onTap) {
          onTap(recipe, { joinSessionId: session.id, view: "cook" });
        }
      }}
      className="mcm-focusable"
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: COLOR.deep,
        border: `1.5px solid ${palette.ring}`,
        borderRadius: 18,
        padding: "14px 16px",
        marginBottom: 12,
        cursor: "pointer",
        fontFamily: FONT.detail,
      }}
    >
      {/* Header: status dot + recipe name */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
      }}>
        <span style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          background: palette.ring,
          flex: "0 0 auto",
        }} />
        <span style={{
          fontFamily: FONT.mono,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: COLOR.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        }}>
          {recipeTitle}
        </span>
        {ownerName ? (
          <span style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: palette.ring,
            flex: "0 0 auto",
          }}>
            {ownerName}
          </span>
        ) : null}
      </div>

      {/* Headline + countdown */}
      <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        marginBottom: 4,
      }}>
        <span style={{
          fontFamily: FONT.serif || FONT.detail,
          fontStyle: "italic",
          fontSize: 16,
          fontWeight: 600,
          color: COLOR.ink,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {stepLabel}
        </span>
        {remainingSec != null ? (
          <span style={{
            fontFamily: FONT.mono,
            fontSize: 22,
            fontWeight: 800,
            color: COLOR.ink,
            fontVariantNumeric: "tabular-nums",
          }}>
            {fmtMmSs(remainingSec)}
          </span>
        ) : null}
      </div>

      {/* Sub-line: total time remaining + secondary parallel timers.
          When the cook has multiple steps with timers running at
          once (e.g. step 1 ticking AND step 3 ticking), the active
          step's timer is the BIG countdown above; the OTHER running
          timers render here inline as small bold "(N) mm:ss" chips.
          User explicit ask: don't stack them, list them in line and
          name with the step number. */}
      <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 10,
      }}>
        <span style={{
          fontFamily: FONT.mono,
          fontSize: 11,
          color: COLOR.sub,
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}>
          Total {fmtHmS(totalRemaining)}
        </span>
        {parallelTimers
          .filter((row) => {
            // Skip the active step's own row — it's already the big
            // countdown above. Strip stage suffix before matching.
            const baseId = String(row.step_id || "").replace(/-s\d+$/, "");
            return baseId !== baseStepId;
          })
          .map((row) => {
            const baseId = String(row.step_id || "").replace(/-s\d+$/, "");
            const stepIdx = steps.findIndex((s) => String(s.id) === baseId);
            const stepNum = stepIdx >= 0 ? stepIdx + 1 : "?";
            const ends = row.timer_state?.endsAt
              ? new Date(row.timer_state.endsAt).getTime()
              : 0;
            const remaining = Math.max(0, Math.ceil((ends - now) / 1000));
            return (
              <span
                key={row.step_id}
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 11,
                  fontWeight: 800,
                  color: palette.ring,
                  letterSpacing: 0.4,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                ({stepNum}) {fmtMmSs(remaining)}
              </span>
            );
          })}
      </div>

      {/* Proportional time-weighted progress bar */}
      <ProgressBar
        steps={steps}
        perStepSec={perStepSec}
        totalSec={totalSec}
        activeStepIdx={activeStepIdx}
        activeStepPct={activeStepPct}
        color={palette.ring}
        tint={palette.tint}
        participantPins={participantPins.map((pin) => {
          // Map each participant's step_id → bar position. Strip
          // TimerStack stage suffix, find the recipe step idx, then
          // compute pct within that step ONLY from a real ticking
          // timer. Falling back to started_at (step entry time)
          // caused the bar to auto-tick the moment someone opened
          // the step — wrong, since the user hadn't tapped Start.
          const baseId = String(pin.stepId || "").replace(/-s\d+$/, "");
          const sIdx = baseId
            ? steps.findIndex((s) => String(s.id) === baseId)
            : -1;
          let pct = 0;
          if (sIdx >= 0 && pin.nominalSec > 0 && pin.timerEndsAt) {
            const endsMs = new Date(pin.timerEndsAt).getTime();
            const remainingS = Math.max(0, (endsMs - now) / 1000);
            const elapsed = Math.max(0, Math.min(pin.nominalSec, pin.nominalSec - remainingS));
            pct = elapsed / pin.nominalSec;
          }
          return {
            userId: pin.userId,
            color: pin.color,
            avatarUrl: pin.avatarUrl,
            initial: (pin.name || "").charAt(0).toUpperCase(),
            stepIdx: sIdx,
            pct,
            finished: pin.finished,
          };
        })}
      />
    </button>
  );
}

function ProgressBar({
  steps, perStepSec, totalSec,
  activeStepIdx, activeStepPct,
  color, tint,
  participantPins = [],
}) {
  const TRACK_H = 16;
  if (steps.length === 0) {
    return (
      <div style={{
        width: "100%",
        height: TRACK_H,
        borderRadius: TRACK_H / 2,
        background: tint,
      }} />
    );
  }

  // Cumulative-percent layout — every fill / dot / avatar is
  // absolutely positioned against the same track so the avatar
  // can ride the right edge of the active fill (the "ticker").
  const startPct = [];
  const widthPct = [];
  let acc = 0;
  for (let i = 0; i < steps.length; i++) {
    const w = perStepSec[i] / totalSec;
    startPct.push(acc);
    widthPct.push(w);
    acc += w;
  }

  // Where the avatar lives. If we have an active step, ride the
  // right edge of its fill. Otherwise (no active step yet), park
  // it at the left edge of the bar.
  const tickerPct = activeStepIdx >= 0
    ? Math.max(0, Math.min(1, startPct[activeStepIdx] + widthPct[activeStepIdx] * activeStepPct))
    : 0;

  const AVATAR_D = 22;
  const DOT_D = 6;

  return (
    <div style={{
      position: "relative",
      width: "100%",
      height: AVATAR_D,
      paddingTop: (AVATAR_D - TRACK_H) / 2,
    }}>
      {/* Track (tint background covers the whole rail) */}
      <div style={{
        position: "absolute",
        left: 0, right: 0,
        top: (AVATAR_D - TRACK_H) / 2,
        height: TRACK_H,
        borderRadius: TRACK_H / 2,
        background: tint,
        overflow: "hidden",
      }}>
        {/* Done segments — solid color rectangles */}
        {steps.map((_, i) => {
          if (i >= activeStepIdx || activeStepIdx < 0) return null;
          return (
            <div
              key={`done-${i}`}
              style={{
                position: "absolute",
                left: `${startPct[i] * 100}%`,
                width: `${widthPct[i] * 100}%`,
                top: 0, bottom: 0,
                background: color,
              }}
            />
          );
        })}
        {/* Active segment fill — grows left → right with the timer */}
        {activeStepIdx >= 0 ? (
          <div
            style={{
              position: "absolute",
              left: `${startPct[activeStepIdx] * 100}%`,
              width: `${widthPct[activeStepIdx] * activeStepPct * 100}%`,
              top: 0, bottom: 0,
              background: color,
              transition: "width 0.4s linear",
            }}
          />
        ) : null}
      </div>

      {/* Boundary dots between segments (skip i=0 — no left edge dot) */}
      {steps.map((_, i) => {
        if (i === 0) return null;
        return (
          <div
            key={`dot-${i}`}
            style={{
              position: "absolute",
              left: `${startPct[i] * 100}%`,
              top: (AVATAR_D - DOT_D) / 2,
              width: DOT_D,
              height: DOT_D,
              marginLeft: -DOT_D / 2,
              borderRadius: DOT_D / 2,
              background: color,
              opacity: 0.55,
            }}
          />
        );
      })}

      {/* Checkmark markers — one per completed step, parked at the
          CENTER of that step's segment. Mirrors the lock-screen
          widget's checkmark design (commit f77e291 → owner-tinted
          circle + white check). Boundary dots get covered when a
          check lands on the same x; ticker avatars draw later so
          they layer on top if they ever align (rare). */}
      {steps.map((_, i) => {
        if (i >= activeStepIdx || activeStepIdx < 0) return null;
        const CHECK_D = 18;
        const centerLeftPct = startPct[i] + widthPct[i] / 2;
        return (
          <div
            key={`check-${i}`}
            style={{
              position: "absolute",
              left: `${centerLeftPct * 100}%`,
              top: (AVATAR_D - CHECK_D) / 2,
              width: CHECK_D,
              height: CHECK_D,
              marginLeft: -CHECK_D / 2,
              borderRadius: CHECK_D / 2,
              background: color,
              border: `2px solid ${COLOR.deep}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontFamily: FONT.mono,
              fontSize: 10,
              fontWeight: 900,
              lineHeight: 1,
              boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
            }}
          >
            ✓
          </div>
        );
      })}

      {/* End-of-rail dot */}
      <div style={{
        position: "absolute",
        left: "100%",
        top: (AVATAR_D - DOT_D) / 2,
        width: DOT_D,
        height: DOT_D,
        marginLeft: -DOT_D,
        borderRadius: DOT_D / 2,
        background: color,
        opacity: 0.55,
      }} />

      {/* Per-participant ticker avatars — one per cook on this
          session, each at their own current step's position with
          their own color. Falls back to the single-owner ticker
          when no participant pins are provided (older callers). */}
      {participantPins.length > 0 ? (
        participantPins.map((pin) => {
          if (pin.stepIdx < 0) return null;
          const segStart = startPct[pin.stepIdx];
          const segWidth = widthPct[pin.stepIdx];
          const leftPct = Math.max(0, Math.min(1, segStart + segWidth * pin.pct));
          return (
            <TickerAvatar
              key={pin.userId}
              left={`${leftPct * 100}%`}
              size={AVATAR_D}
              color={pin.color}
              imageUrl={pin.avatarUrl}
              initial={pin.initial}
            />
          );
        })
      ) : activeStepIdx >= 0 ? (
        <TickerAvatar
          left={`${tickerPct * 100}%`}
          size={AVATAR_D}
          color={color}
        />
      ) : null}
    </div>
  );
}

// Ticker avatar — circular, sized to fit on the progress rail. Renders
// the owner's profile picture when one resolves; falls back to the
// owner-initial inside a colored circle when no image is available
// (or when the image fails to load — onError flips us to the
// fallback). Keeps the same colored ring around the circle so the
// per-cook color identity reads either way.
function TickerAvatar({ left, size, color, imageUrl, initial }) {
  const [broken, setBroken] = useState(false);
  const showImage = imageUrl && !broken;
  const baseStyle = {
    position: "absolute",
    left,
    top: 0,
    width: size,
    height: size,
    marginLeft: -size / 2,
    borderRadius: size / 2,
    border: `2px solid ${COLOR.deep}`,
    transition: "left 0.4s linear",
    boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
    background: color,
    overflow: "hidden",
  };
  if (showImage) {
    return (
      <div style={baseStyle}>
        <img
          src={imageUrl}
          alt=""
          onError={() => setBroken(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        ...baseStyle,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: COLOR.deep,
        fontFamily: FONT.mono,
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      {initial || ""}
    </div>
  );
}
